/**
 * Mock event bus — WebSocket-backed transport.
 *
 * Why not BroadcastChannel? BroadcastChannel is same-origin only, but our
 * widget runs on the demo's origin (e.g. http://localhost:5173) while the
 * agent app is at http://localhost:5175 — different origins, so the
 * channels never see each other. A tiny dev relay (scripts/bus-relay.mjs)
 * fans out messages between all connected clients on ws://localhost:5172.
 *
 * The wire format and event shapes mirror Socket.IO. Phase 5 swaps the
 * relay for a real Socket.IO server with the same fan-out semantics.
 *
 * Network conditions (latency / packet loss / hard disconnect) are layered
 * on top in the same place the dev panel can mutate them.
 */

import type { BusEvent, BusEventName, BusEventOf } from './schema';

export type BusUnsubscribe = () => void;
export type BusListener<T extends BusEventName = BusEventName> = (
  event: BusEventOf<T>,
) => void;

export interface EventBus {
  emit<T extends BusEventName>(event: BusEventOf<T>): void;
  on<T extends BusEventName>(name: T, listener: BusListener<T>): BusUnsubscribe;
  once<T extends BusEventName>(name: T, listener: BusListener<T>): BusUnsubscribe;
  off<T extends BusEventName>(name: T, listener: BusListener<T>): void;
  close(): void;
  setNetworkConditions(conditions: Partial<NetworkConditions>): void;
  getNetworkConditions(): NetworkConditions;
  inspect(): { listenerCount: Record<string, number>; connected: boolean };
  onConditionsChange(listener: (c: NetworkConditions) => void): BusUnsubscribe;
  /** Connection state for the dev panel. */
  onConnectionChange(listener: (state: ConnectionState) => void): BusUnsubscribe;
  getConnectionState(): ConnectionState;
}

export type ConnectionState = 'connecting' | 'open' | 'closed' | 'disconnected';

export interface NetworkConditions {
  minLatency: number;
  maxLatency: number;
  /** 0…1 probability that an outgoing emit is silently dropped. */
  packetLoss: number;
  /** When true, emits are dropped AND incoming events are ignored. */
  disconnected: boolean;
}

const DEFAULT_RELAY_URL =
  typeof window !== 'undefined' ? `ws://${window.location.hostname}:5172` : 'ws://127.0.0.1:5172';

const DEFAULT_CONDITIONS: NetworkConditions = {
  minLatency: 100,
  maxLatency: 400,
  packetLoss: 0,
  disconnected: false,
};

const instances = new Map<string, EventBus>();

/**
 * Get-or-create the singleton bus. The argument is a connection identifier
 * (used as a kind of channel "name" inside one process). In the WebSocket
 * implementation the relay fans every message to every client so the name is
 * mostly cosmetic, but it lets us mock multiple disjoint buses in tests.
 */
export function getBus(name: string = 'prakash-livechat-mock'): EventBus {
  let bus = instances.get(name);
  if (bus) return bus;
  bus = createBus();
  instances.set(name, bus);
  return bus;
}

export function disposeBus(name: string = 'prakash-livechat-mock'): void {
  const bus = instances.get(name);
  if (!bus) return;
  bus.close();
  instances.delete(name);
}

type AnyBusListener = (event: BusEvent) => void;

function createBus(): EventBus {
  const listeners = new Map<BusEventName, Set<AnyBusListener>>();
  const conditionListeners = new Set<(c: NetworkConditions) => void>();
  const connectionListeners = new Set<(s: ConnectionState) => void>();
  let conditions: NetworkConditions = { ...DEFAULT_CONDITIONS };
  let connection: ConnectionState = 'connecting';
  let closed = false;
  let ws: WebSocket | null = null;
  let reconnectTimer: number | null = null;
  const pendingOutbox: BusEvent[] = [];

  function setConnection(next: ConnectionState) {
    if (connection === next) return;
    connection = next;
    [...connectionListeners].forEach((fn) => fn(next));
  }

  function openSocket() {
    if (closed) return;
    if (typeof WebSocket === 'undefined') {
      // SSR / non-browser env — silently no-op. The same module shape lets
      // server code build against the interface in tests.
      setConnection('closed');
      return;
    }
    setConnection('connecting');
    try {
      ws = new WebSocket(DEFAULT_RELAY_URL);
    } catch {
      scheduleReconnect();
      return;
    }
    ws.addEventListener('open', () => {
      setConnection(conditions.disconnected ? 'disconnected' : 'open');
      // Flush anything that queued up while disconnected.
      const queued = pendingOutbox.splice(0);
      for (const event of queued) {
        sendNow(event);
      }
    });
    ws.addEventListener('message', (ev) => {
      if (conditions.disconnected) return;
      try {
        const event = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as BusEvent;
        if (!event || typeof event.type !== 'string') return;
        dispatch(event);
      } catch {
        /* drop malformed */
      }
    });
    ws.addEventListener('close', () => {
      ws = null;
      if (closed) return;
      setConnection('closed');
      scheduleReconnect();
    });
    ws.addEventListener('error', () => {
      // Let 'close' handle reconnect — error fires alongside it.
    });
  }

  function scheduleReconnect() {
    if (closed) return;
    if (reconnectTimer !== null) return;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      openSocket();
    }, 1500);
  }

  function dispatch(event: BusEvent) {
    const set = listeners.get(event.type);
    if (!set || set.size === 0) return;
    [...set].forEach((fn) => {
      try {
        fn(event);
      } catch (err) {
        console.error('[event-bus] listener threw', err);
      }
    });
  }

  function chooseLatency(): number {
    const { minLatency, maxLatency } = conditions;
    if (maxLatency <= minLatency) return Math.max(0, minLatency);
    return minLatency + Math.random() * (maxLatency - minLatency);
  }

  function sendNow(event: BusEvent) {
    if (closed) return;
    if (!ws || ws.readyState !== 1) {
      pendingOutbox.push(event);
      return;
    }
    try {
      ws.send(JSON.stringify(event));
    } catch {
      pendingOutbox.push(event);
    }
  }

  function emitWithLatency(event: BusEvent) {
    if (closed) return;
    if (conditions.disconnected) return;
    if (Math.random() < conditions.packetLoss) return;
    const delay = chooseLatency();
    window.setTimeout(() => {
      if (closed || conditions.disconnected) return;
      // Local dispatch: the relay doesn't echo back to the sender, so without
      // this the originating tab would never see its own emit (e.g. the AI
      // orchestrator's reply needs to land in the same tab's Conversation
      // store as well as the visitor's widget tab).
      dispatch(event);
      sendNow(event);
    }, delay);
  }

  function notifyConditionsChange() {
    const snapshot = { ...conditions };
    [...conditionListeners].forEach((fn) => fn(snapshot));
  }

  // Connect eagerly.
  openSocket();

  return {
    emit(event) {
      emitWithLatency(event as BusEvent);
    },
    on(name, listener) {
      let set = listeners.get(name);
      if (!set) {
        set = new Set();
        listeners.set(name, set);
      }
      const fn = listener as AnyBusListener;
      set.add(fn);
      return () => {
        set!.delete(fn);
      };
    },
    once(name, listener) {
      const fn = listener as AnyBusListener;
      let set = listeners.get(name);
      if (!set) {
        set = new Set();
        listeners.set(name, set);
      }
      const wrapped: AnyBusListener = (event) => {
        off();
        fn(event);
      };
      set.add(wrapped);
      const off = () => {
        set!.delete(wrapped);
      };
      return off;
    },
    off(name, listener) {
      const set = listeners.get(name);
      set?.delete(listener as AnyBusListener);
    },
    close() {
      closed = true;
      listeners.clear();
      conditionListeners.clear();
      connectionListeners.clear();
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (ws) {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
        ws = null;
      }
    },
    setNetworkConditions(patch) {
      conditions = { ...conditions, ...patch };
      // Reflect disconnect into the user-visible connection state.
      if (conditions.disconnected) {
        setConnection('disconnected');
      } else if (ws?.readyState === 1) {
        setConnection('open');
      }
      notifyConditionsChange();
    },
    getNetworkConditions() {
      return { ...conditions };
    },
    inspect() {
      const listenerCount: Record<string, number> = {};
      for (const [n, set] of listeners) listenerCount[n] = set.size;
      return { listenerCount, connected: connection === 'open' };
    },
    onConditionsChange(listener) {
      conditionListeners.add(listener);
      return () => conditionListeners.delete(listener);
    },
    onConnectionChange(listener) {
      connectionListeners.add(listener);
      // Fire current state immediately so subscribers don't miss the first edge.
      try {
        listener(connection);
      } catch {
        /* ignore */
      }
      return () => connectionListeners.delete(listener);
    },
    getConnectionState() {
      return connection;
    },
  };
}

export type { BusEvent, BusEventName, BusEventOf } from './schema';
